import React, { useEffect, useMemo, useState } from "react";
import { useGetDockUtilisationQuery } from "../../api/vmsSlice";
import DayPicker from "./DayPicker";
import DockUtilisation from "./DockUtilisation";
import DwellBreakdown from "./DwellBreakdown";
import useDaySelection from "./useDaySelection";
import { DEFAULT_FILTERS, analyticsQueryArgs } from "./analyticsFilters";

// One plant-local day and Bay selection for both Overview panels.
export default function OverviewAnalytics({ plantId, canView }) {
	const { date, setDate, requestedDate } = useDaySelection();
	const [selectedBay, setSelectedBay] = useState(DEFAULT_FILTERS.selectedBay);
	const filters = useMemo(() => ({ date, selectedBay }), [date, selectedBay]);

	// Share the utilisation panel's cache entry for plant-local bounds and available Bays.
	const query = useGetDockUtilisationQuery(
		analyticsQueryArgs({ plantId, ...filters }),
		{ skip: !plantId || !canView },
	);
	const data = query.currentData?.data;
	const bays = useMemo(() => data?.bays || [], [data]);

	// Plant configuration can change under a long-open tab, and a chip the backend would now reject
	// must not stay pressed — on either panel, which is the point of holding it once.
	useEffect(() => {
		if (selectedBay && bays.length && !bays.some((bay) => bay.checkpointNumber === selectedBay)) {
			setSelectedBay(null);
		}
	}, [bays, selectedBay]);

	if (!canView) return null;

	return (
		<>
			<section className="dock-analytics-filters" aria-label="Analytics filters">
				{/* Rendered from the response, so the row can never offer a Bay the backend would reject. */}
				{bays.length > 1 && (
					<div className="dock-filter-chips dock-peak-bays" role="group" aria-label="Filter by Bay">
						<button
							type="button"
							className="dock-chip"
							aria-pressed={selectedBay === null}
							onClick={() => setSelectedBay(null)}
						>
							All bays
						</button>
						{bays.map((bay) => (
							<button
								key={bay.checkpointNumber}
								type="button"
								className="dock-chip"
								aria-pressed={selectedBay === bay.checkpointNumber}
								onClick={() => setSelectedBay(bay.checkpointNumber)}
							>
								{bay.displayName}
							</button>
						))}
					</div>
				)}
				<div className="dock-analytics-filters-row">
					<DayPicker meta={data} requestedDate={requestedDate} onChange={setDate} label="Day reported on" />
				</div>
				<span className="dock-analytics-filters-note">
					Applies to both panels below
					{query.currentData?.timezone ? ` · ${query.currentData.timezone.replace("_", " ")}` : ""}
				</span>
			</section>

			<DockUtilisation plantId={plantId} canView={canView} filters={filters} />
			<DwellBreakdown plantId={plantId} canView={canView} filters={filters} />
		</>
	);
}
